// WHAT: Custom hook for managing aspect-related operations within TimeTracQ workspaces
// WHY:  Centralizes logic for updating, adding, and toggling aspect properties, ensuring consistency
// DOMAIN: Settings → TimeTracQ
// DEPENDENCIES: React hooks, Tauri core API, WorkspaceConfig and AspectConfig types
// RISKS:
//   - WARN: Aspect updates are immediate but not saved until an explicit save action is performed.
//   - WARN: No validation of aspect name format or uniqueness within a workspace when adding.
//   - NOTE: Error handling may not provide specific failure reasons for backend commands.

import type { AspectConfig, WorkspaceConfig } from "../types";

import { core } from "@tauri-apps/api";
import { useCallback } from "react";

/**
 * WHAT:
 *   Custom hook to manage the creation and modification of aspects within the TimeTracQ workspace configuration.
 *
 * WHY:
 *   Encapsulates the logic for updating individual aspect properties and adding new aspects,
 *   promoting reusability and maintaining a clean separation of concerns from the main UI component.
 *
 * PARAMETERS:
 *   - config: The current `WorkspaceConfig` state.
 *   - setConfig: A state setter function for `WorkspaceConfig`.
 *   - setMessage: A state setter function for message state.
 *   - setSaving: A state setter function for the saving status.
 *
 * RETURNS:
 *   An object containing functions to update aspect properties and add new aspects.
 *
 * @returns {object} Object with `addAspect`, `toggleAspectBoolean`, `updateAspectColor`, `updateAspectHours`, `updateAspectStatus` functions
 *
 * RISKS:
 *   - WARN: Changes made via `updateAspect` are immediate in the local state but are not persisted
 *           to the database until `saveConfig` (from `useWorkspaceConfig`) is explicitly called.
 *   - WARN: `addAspect` performs basic trimming but no comprehensive validation of aspect names
 *           (e.g., for special characters or uniqueness within a workspace).
 *
 * @param config
 *
 * @param setConfig
 *
 * @param setMessage
 *
 * @param setSaving
 *
 */
export function useAspectManagement(
  config: null | WorkspaceConfig,
  setConfig: React.Dispatch<React.SetStateAction<null | WorkspaceConfig>>,
  setMessage: React.Dispatch<
    React.SetStateAction<null | { type: "error" | "success"; text: string }>
  >,
  setSaving: React.Dispatch<React.SetStateAction<boolean>>,
) {
  /**
   * WHAT:
   *   Updates a specific aspect field value in the configuration state.
   *
   * WHY:
   *   Provides a type-safe way to update aspect properties while maintaining immutability of the state.
   *
   * INPUTS:
   *   - workspaceName: string - The name of the workspace containing the aspect.
   *   - aspectName: string - The name of the aspect to update.
   *   - field: keyof AspectConfig - The field name (key) of the aspect configuration to update.
   *   - value: unknown - The new value for the specified field.
   *
   * SIDE EFFECTS:
   *   - Updates the aspect configuration in the component's local state.
   *   - Creates a new config object to ensure immutability, triggering a re-render.
   *
   * RISKS:
   *   - NOTE: Changes are immediate in the local state but are not persisted until a save operation is performed.
   *   - WARN: No validation of the `value` type or range is performed, which could lead to invalid data.
   */
  const updateAspect = useCallback(
    (
      workspaceName: string,
      aspectName: string,
      field: keyof AspectConfig,
      value: boolean | number | string,
    ) => {
      if (!config) {
        return;
      }

      setConfig((previous) => {
        if (!previous) {
          return previous;
        }

        const newConfig = { ...previous };
        const workspace = newConfig.workspaces[workspaceName];
        if (workspace?.[aspectName]) {
          workspace[aspectName] = {
            ...workspace[aspectName],
            [field]: value,
          };
        }
        return newConfig;
      });
    },
    [config, setConfig],
  );

  const updateAspectHours = useCallback(
    (workspaceName: string, aspectName: string, hours: string) => {
      const numberValue = Number.parseFloat(hours);
      if (!Number.isNaN(numberValue)) {
        updateAspect(workspaceName, aspectName, "hours", numberValue);
      }
    },
    [updateAspect],
  );

  const updateAspectStatus = useCallback(
    (workspaceName: string, aspectName: string, status: string) => {
      updateAspect(workspaceName, aspectName, "status", status);
    },
    [updateAspect],
  );

  const updateAspectColor = useCallback(
    (workspaceName: string, aspectName: string, color: string) => {
      updateAspect(workspaceName, aspectName, "color", color);
    },
    [updateAspect],
  );

  const toggleAspectBoolean = useCallback(
    (
      workspaceName: string,
      aspectName: string,
      field: "carryover" | "count_hours" | "count_plan",
    ) => {
      if (!config) {
        return;
      }

      const currentValue = config.workspaces[workspaceName]?.[aspectName]?.[field];
      if (currentValue !== undefined) {
        updateAspect(workspaceName, aspectName, field, !currentValue);
      }
    },
    [config, updateAspect],
  );

  /**
   * WHAT:
   *   Creates a new aspect in a specified workspace with the given configuration.
   *
   * WHY:
   *   Allows users to add new aspects to their workspaces for better time tracking organization.
   *
   * INPUTS:
   *   - workspaceName: string - The name of the workspace to add the aspect to.
   *   - aspectName: string - The name of the new aspect.
   *   - aspectConfig: AspectConfig - The configuration object for the new aspect.
   *   - onSuccessCallback: () => Promise<void> - A callback function to execute on successful aspect creation.
   *
   * SIDE EFFECTS:
   *   - Invokes a backend command (`add_aspect_cmd`) to create the aspect in the database.
   *   - Updates success/error messages in the UI.
   *   - Executes `onSuccessCallback` which typically reloads configuration and updates UI state.
   *
   * RISKS:
   *   - WARN: No validation of aspect name format or uniqueness within the workspace is performed before sending to backend.
   *   - NOTE: Error handling may not provide specific failure reasons from the backend command.
   */
  const addAspect = useCallback(
    async (
      workspaceName: string,
      aspectName: string,
      aspectConfig: AspectConfig,
      onSuccessCallback: () => Promise<void>,
    ) => {
      if (!workspaceName || !aspectName.trim()) {
        setMessage({
          text: "Workspace und Aspect Name sind erforderlich",
          type: "error",
        });
        return;
      }

      try {
        setSaving(true);
        setMessage(null);

        await core.invoke("add_aspect_cmd", {
          aspectConfig,
          aspectName: aspectName.trim(),
          workspaceName,
        });
        setMessage({
          text: `Aspect "${aspectName}" erfolgreich erstellt!`,
          type: "success",
        });
        await onSuccessCallback();
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Unbekannter Fehler beim Erstellen des Aspects";
        setMessage({
          text: `Fehler beim Erstellen des Aspects: ${message}`,
          type: "error",
        });
      } finally {
        setSaving(false);
      }
    },
    [setMessage, setSaving],
  );

  return {
    addAspect,
    toggleAspectBoolean,
    updateAspectColor,
    updateAspectHours,
    updateAspectStatus,
  };
}
